import { useEffect, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { PerspectiveCamera } from "@react-three/drei";
import { useWASD } from "../lib/functions";
import { useBox } from "@react-three/cannon";
import { usePlayerContext } from "../lib/context/PlayerContext";
import { MoveDir, LookDelta } from "./GameControls";

// Sensitivity for the look joystick / drag
const LOOK_SENSITIVITY = 0.005;
const MAX_PITCH = THREE.MathUtils.degToRad(70);
const JUMP_VELOCITY = 7;
// Player is considered "grounded" when vertical velocity is near zero
const GROUND_THRESHOLD = 0.3;

export default function Player({
	moveRef,
	lookDeltaRef,
	jumpRef,
}: {
	/** Current directional input (D-pad buttons or WASD keys) */
	moveRef: React.MutableRefObject<MoveDir>;
	/** Accumulated look delta from joystick / mouse drag */
	lookDeltaRef: React.MutableRefObject<LookDelta>;
	/** Set to true by GameControls jump button or Space key; Player resets it after consuming */
	jumpRef: React.MutableRefObject<boolean>;
}) {
	const cam = useRef<THREE.PerspectiveCamera>(null!);
	const { forward, backward, left, right, sprint, jump: jumpKey } = useWASD();

	const [ref, api] = useBox(() => ({
		mass: 1,
		args: [0.5, 2.2, 0.5],
		position: [0, 1.1, 19],
		linearDamping: 0.8,
		angularDamping: 1,
		fixedRotation: true,
	}));

	// Reuse vectors across frames to avoid GC pressure
	const dir = useRef(new THREE.Vector3()).current;
	const rightVec = useRef(new THREE.Vector3()).current;
	const moveDir = useRef(new THREE.Vector3()).current;
	const up = useRef(new THREE.Vector3(0, 1, 0)).current;

	const vel = useRef<[number, number, number]>([0, 0, 0]);
	const pos = useRef<[number, number, number]>([0, 0, 0]);

	// Camera euler persisted across frames (yaw + pitch)
	const yaw = useRef(0);
	const pitch = useRef(0);

	const { registerPlayerApi } = usePlayerContext();

	useEffect(() => { registerPlayerApi(api); }, [api, registerPlayerApi]);
	useEffect(() => api.velocity.subscribe((v) => (vel.current = v)), [api]);
	useEffect(() => api.position.subscribe((p) => (pos.current = p)), [api]);

	useFrame((_, delta) => {
		if (!cam.current) return;

		// ── 1. Apply accumulated look delta ──────────────────────────────────
		const { dx, dy } = lookDeltaRef.current;
		if (dx !== 0 || dy !== 0) {
			yaw.current -= dx * LOOK_SENSITIVITY;
			pitch.current -= dy * LOOK_SENSITIVITY;
			pitch.current = Math.max(-MAX_PITCH, Math.min(MAX_PITCH, pitch.current));
			lookDeltaRef.current = { dx: 0, dy: 0 };
		}
		cam.current.rotation.set(pitch.current, yaw.current, 0, "YXZ");

		// ── 2. Compute movement direction ─────────────────────────────────────
		cam.current.getWorldDirection(dir);
		dir.y = 0;
		dir.normalize();
		rightVec.crossVectors(up, dir).normalize();

		// Combine button input (D-pad) and keyboard (WASD / arrows)
		const m = moveRef.current;
		let mx = 0, mz = 0;
		if (m.forward || forward()) mz += 1;
		if (m.backward || backward()) mz -= 1;
		if (m.left || left()) mx += 1;
		if (m.right || right()) mx -= 1;

		const baseSpeed = sprint() ? 20 : 17;

		moveDir.set(0, 0, 0);
		moveDir.addScaledVector(dir, mz);
		moveDir.addScaledVector(rightVec, mx);

		if (moveDir.lengthSq() > 0) {
			moveDir.normalize().multiplyScalar(baseSpeed);
		}

		// ── 3. Jump ───────────────────────────────────────────────────────────
		const isGrounded = Math.abs(vel.current[1]) < GROUND_THRESHOLD;
		if ((jumpRef.current || jumpKey()) && isGrounded) {
			api.velocity.set(moveDir.x, JUMP_VELOCITY, moveDir.z);
			jumpRef.current = false;
		} else {
			api.velocity.set(moveDir.x, vel.current[1], moveDir.z);
			jumpRef.current = false;
		}
	});

	useEffect(() => {
		const handleResize = () => {
			if (cam.current) {
				cam.current.aspect = window.innerWidth / window.innerHeight;
				cam.current.updateProjectionMatrix();
			}
		};
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	return (
		<group ref={ref} position={[0, 1.1, 19]}>
			<PerspectiveCamera ref={cam} makeDefault fov={55} position={[0, 0.5, 0]}>
				{/* Crosshair */}
				<group position={[0, 0, -1]}>
					<mesh raycast={() => null}>
						<planeGeometry args={[0.005, 0.05]} />
						<meshBasicMaterial color="white" />
					</mesh>
					<mesh raycast={() => null}>
						<planeGeometry args={[0.05, 0.005]} />
						<meshBasicMaterial color="white" />
					</mesh>
				</group>
			</PerspectiveCamera>
		</group>
	);
}
